import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getAttempt, getQuestionsByIds } from "../lib/db";
import type { Attempt, Question } from "../lib/types";
import { QuestionPhoto } from "../components/QuestionPhoto";
import "./Quiz.css";

export default function Review() {
  const { attemptId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!attemptId) return;
      try {
        const att = await getAttempt(attemptId);
        if (!alive) return;
        if (!att || att.userId !== user?.uid) {
          setError("No encontramos este resultado.");
          setLoading(false);
          return;
        }
        const qs = await getQuestionsByIds(att.questionIds);
        const ordered = att.questionIds
          .map((id) => qs.find((q) => q.id === id))
          .filter((q): q is Question => !!q);
        if (!alive) return;
        setAttempt(att);
        setQuestions(ordered);
      } catch (err) {
        console.error(err);
        if (alive) setError("No se pudo cargar el resultado.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [attemptId, user]);

  if (loading) {
    return <div className="quiz"><div className="spinner" style={{ margin: "80px auto" }} /></div>;
  }
  if (error || !attempt) {
    return (
      <div className="quiz quiz--message">
        <p>{error ?? "No encontramos este resultado."}</p>
        <button className="btn" onClick={() => navigate("/tests")}>
          Volver al inicio
        </button>
      </div>
    );
  }

  const pct = Math.round((attempt.score / attempt.total) * 100);

  return (
    <div className="quiz">
      <div className="quiz-topbar">
        <div className="quiz-topbar__side quiz-topbar__left">
          <strong>{attempt.courseTitle}</strong>
          <span>Resultados</span>
        </div>
        <div className="quiz-topbar__progress">
          <div className="quiz-topbar__track">
            <div className="quiz-topbar__fill" style={{ width: "100%" }} />
          </div>
        </div>
        <div className="quiz-topbar__side quiz-topbar__right">
          <strong>Resultado: {pct}%</strong>
          <span>
            {attempt.score} / {attempt.total}
          </span>
        </div>
      </div>

      {questions.map((q, i) => {
        const ans = attempt.answers[q.id];
        return (
          <div key={q.id} className="review-q">
            <p className="review-q__label">Pregunta nº {i + 1}</p>
            <h2 className="review-q__text">{q.text}</h2>
            {q.imageUrl && <QuestionPhoto src={q.imageUrl} />}

            {q.type === "text" ? (
              <>
                <div
                  className={
                    "review__answerbox " +
                    (ans?.correct
                      ? "review__answerbox--correct"
                      : "review__answerbox--wrong")
                  }
                >
                  <span>
                    <small>Tu respuesta</small>
                    {ans?.text || "—"}
                  </span>
                  <span
                    className={
                      "pill__mark " +
                      (ans?.correct ? "pill__mark--correct" : "pill__mark--wrong")
                    }
                  >
                    {ans?.correct ? "✓" : "✕"}
                  </span>
                </div>
                {!ans?.correct && q.acceptedAnswers?.[0] && (
                  <div className="review__answerbox review__answerbox--correct">
                    <span>
                      <small>Respuesta correcta</small>
                      {q.acceptedAnswers[0]}
                    </span>
                    <span className="pill__mark pill__mark--correct">✓</span>
                  </div>
                )}
              </>
            ) : (
              <div className="choice-list">
                {q.choices?.map((c) => {
                  const picked = ans?.choiceId === c.id;
                  const showCorrect = c.correct;
                  const showWrong = picked && !c.correct;
                  return (
                    <div
                      key={c.id}
                      className={
                        "pill" +
                        (showCorrect ? " pill--correct" : "") +
                        (showWrong ? " pill--wrong" : "")
                      }
                    >
                      {c.text}
                      {showCorrect && (
                        <span className="pill__mark pill__mark--correct">✓</span>
                      )}
                      {showWrong && (
                        <span className="pill__mark pill__mark--wrong">✕</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <div className="review__back">
        <button
          className="btn btn--muted btn--block"
          onClick={() => navigate("/tests")}
        >
          Volver al inicio
        </button>
      </div>
    </div>
  );
}
